import { useCallback, useEffect, useState } from 'react';
import { api, OPS_NEXT, REJECTION_REASONS, type WorkItem, type WorkItemEvent } from '../api';
import { useToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Badge, EmptyState, LoadingPanel, PageHeader } from '../ui/bits';
import { PayloadView } from '../ui/PayloadView';

interface QueueRow {
  item: WorkItem;
  tenantSlug: string;
  tenantName: string;
}

interface DecidedRow {
  eventId: string;
  decidedAt: string;
  decision: string;
  note: string | null;
  actor: string;
  actorName: string | null;
  item: WorkItem;
  tenantName: string;
}

export function ReviewQueue() {
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [decided, setDecided] = useState<DecidedRow[] | null>(null);
  const [selected, setSelected] = useState<{ item: WorkItem; tenantName: string } | null>(null);
  const toast = useToast();

  const load = useCallback(
    () =>
      Promise.all([
        api.get<QueueRow[]>('/api/work-items/review-queue').then(setRows),
        api.get<DecidedRow[]>('/api/work-items/recently-decided').then(setDecided),
      ]).catch(() => {
        toast.error('Could not load the review queue.');
        setRows([]);
        setDecided([]);
      }),
    // toast api is stable (ref-backed provider), so deps stay empty
    []
  );
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page-stack">
      <PageHeader title="Review Queue" sub={rows ? `${rows.length} awaiting review` : undefined} />
      <div className="grid-2">
        <div className="panel">
          {rows === null && <LoadingPanel />}
          {rows !== null && rows.length === 0 && (
            <EmptyState title="Nothing awaiting review" hint="New drafts land here as agents generate them." />
          )}
          {rows !== null && rows.length > 0 && (
            <div className="table-wrap table-wrap--sticky">
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Type</th>
                    <th>Channel</th>
                    <th>State</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.item.id}
                      tabIndex={0}
                      aria-selected={selected?.item.id === row.item.id}
                      className={`clickable${selected?.item.id === row.item.id ? ' selected' : ''}`}
                      onClick={() => setSelected(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelected(row);
                        }
                      }}
                    >
                      <td>{row.tenantName}</td>
                      <td>{row.item.type.replaceAll('_', ' ')}</td>
                      <td>{row.item.channel ?? '—'}</td>
                      <td>
                        <Badge state={row.item.state} />
                      </td>
                      <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                        {new Date(row.item.updatedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {selected ? (
          <ItemDetail
            key={selected.item.id}
            item={selected.item}
            onChanged={() => {
              setSelected(null);
              void load();
            }}
          />
        ) : (
          <div className="panel">
            <EmptyState title="Select an item to review" hint="Click a row (or focus it and press Enter)." />
          </div>
        )}
      </div>
      <RecentlyDecided rows={decided} selectedId={selected?.item.id} onSelect={setSelected} />
    </div>
  );
}

const DECISION_LABEL: Record<string, string> = {
  approved: 'Approved',
  client_review: 'Sent to client',
  rejected: 'Rejected',
};

/**
 * Where an approval "reappears" after it leaves the queue: the last 25 human
 * decisions, with what became of each item afterwards (published, failed…).
 */
function RecentlyDecided({
  rows,
  selectedId,
  onSelect,
}: {
  rows: DecidedRow[] | null;
  selectedId?: string;
  onSelect: (sel: { item: WorkItem; tenantName: string }) => void;
}) {
  return (
    <div className="panel">
      <h2>Recently decided</h2>
      {rows === null && <LoadingPanel />}
      {rows !== null && rows.length === 0 && (
        <EmptyState title="No decisions yet" hint="Items you approve, reject, or send to a client appear here." />
      )}
      {rows !== null && rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Decided</th>
                <th>Tenant</th>
                <th>Item</th>
                <th>Decision</th>
                <th>By</th>
                <th>Now</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.eventId}
                  tabIndex={0}
                  aria-selected={selectedId === d.item.id}
                  className={`clickable${selectedId === d.item.id ? ' selected' : ''}`}
                  onClick={() => onSelect({ item: d.item, tenantName: d.tenantName })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect({ item: d.item, tenantName: d.tenantName });
                    }
                  }}
                >
                  <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(d.decidedAt).toLocaleString()}
                  </td>
                  <td>{d.tenantName}</td>
                  <td>
                    {d.item.type.replaceAll('_', ' ')}
                    {typeof d.item.payload.title === 'string' && (
                      <span className="muted small"> · {d.item.payload.title}</span>
                    )}
                  </td>
                  <td>
                    <Badge state={d.decision} label={DECISION_LABEL[d.decision]} />
                  </td>
                  <td className="small">{d.actorName ?? (d.actor.includes('-') ? 'ops user' : d.actor)}</td>
                  <td>
                    <Badge state={d.item.state} />
                    {d.note && <span className="muted small"> · {d.note}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface MediaPayload {
  media?: { key: string; disclosure: string; model?: string };
  media_skipped?: string;
  needs_photo?: boolean;
  photo_note?: string | null;
}

/** §17: AI media renders with its MANDATORY disclosure; photo needs surface loudly. */
function MediaBlock({ payload }: { payload: MediaPayload }) {
  if (payload.media?.key) {
    return (
      <figure style={{ margin: '8px 0' }}>
        <img
          src={`/api/admin/media?key=${encodeURIComponent(payload.media.key)}`}
          alt={payload.media.disclosure}
          style={{ maxWidth: 280, borderRadius: 8, border: '1px solid var(--line)' }}
        />
        <figcaption className="muted tiny">
          🤖 {payload.media.disclosure} — disclosure is appended on publish and cannot be removed
        </figcaption>
      </figure>
    );
  }
  if (payload.needs_photo) {
    return (
      <p className="callout callout--warn">
        📷 Real photo required{payload.photo_note ? `: ${payload.photo_note}` : ''}
        {payload.media_skipped ? ` (AI generation blocked: ${payload.media_skipped})` : ''}
      </p>
    );
  }
  return null;
}

export function ItemDetail({ item, onChanged }: { item: WorkItem; onChanged: () => void }) {
  const [payloadText, setPayloadText] = useState(JSON.stringify(item.payload, null, 2));
  const [editing, setEditing] = useState(false);
  const [events, setEvents] = useState<WorkItemEvent[]>([]);
  const [reason, setReason] = useState<string>('quality');
  const [error, setError] = useState<string | null>(null);
  const [confirmReject, setConfirmReject] = useState<string | null>(null); // holds the target state
  const toast = useToast();
  const dirty = payloadText !== JSON.stringify(item.payload, null, 2);

  useEffect(() => {
    api
      .get<WorkItemEvent[]>(`/api/work-items/${item.id}/events?tenantId=${item.tenantId}`)
      .then(setEvents)
      .catch(console.error);
  }, [item.id, item.tenantId]);

  const saveEdit = async () => {
    setError(null);
    try {
      const payload = JSON.parse(payloadText);
      await api.post(`/api/work-items/${item.id}/edit`, { tenantId: item.tenantId, payload });
      toast.success('Edit saved — the diff feeds the agent flywheel.');
      onChanged();
    } catch (err) {
      setError(err instanceof SyntaxError ? 'Payload is not valid JSON' : 'Edit failed');
    }
  };

  const transition = async (to: string, withReason: boolean) => {
    setError(null);
    try {
      await api.post(`/api/work-items/${item.id}/transition`, {
        tenantId: item.tenantId,
        to,
        reason: withReason ? reason : undefined,
      });
      toast.success(`Moved to ${to.replaceAll('_', ' ')}.`);
      onChanged();
    } catch {
      setError(`Transition to ${to} failed`);
    }
  };

  const actions = OPS_NEXT[item.state] ?? [];

  return (
    <div className="panel">
      <h3 className="row-baseline">
        {item.type.replaceAll('_', ' ')} <Badge state={item.state} />
      </h3>
      <p className="muted small" style={{ margin: '4px 0' }}>
        {item.family} · {item.channel ?? 'no channel'} · by {item.generatedBy ?? '—'} · quota {item.quotaPeriod ?? '—'}
      </p>
      <MediaBlock payload={item.payload as MediaPayload} />
      {editing ? (
        <>
          <textarea
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
            rows={14}
            className="mono"
            aria-label="Item payload (JSON)"
            style={{ width: '100%' }}
          />
          <div className="row" style={{ marginTop: 8, marginBottom: 8 }}>
            <button type="button" className="primary" disabled={!dirty} onClick={saveEdit}>
              Save edit (records diff)
            </button>
            <button
              type="button"
              onClick={() => {
                setPayloadText(JSON.stringify(item.payload, null, 2));
                setError(null);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <PayloadView payload={item.payload as Record<string, unknown>} />
      )}
      <div className="row" style={{ marginTop: 8 }}>
        {actions.map((a) =>
          a.reason ? (
            <span key={a.to} className="row" style={{ gap: 4 }}>
              <select value={reason} aria-label="Rejection reason" onChange={(e) => setReason(e.target.value)}>
                {REJECTION_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button type="button" className="danger" onClick={() => setConfirmReject(a.to)}>
                {a.label}
              </button>
            </span>
          ) : (
            <button key={a.to} type="button" onClick={() => transition(a.to, false)}>
              {a.label}
            </button>
          )
        )}
        {!editing && (
          <button type="button" className="btn-sm" onClick={() => setEditing(true)}>
            Edit JSON
          </button>
        )}
      </div>
      {error && (
        <p className="callout callout--danger" role="alert" style={{ marginTop: 8 }}>
          {error}
        </p>
      )}
      {item.error && (
        <pre className="mono callout callout--danger" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(item.error, null, 1)}
        </pre>
      )}
      <h4>Audit trail</h4>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Transition</th>
              <th>Actor</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="small" style={{ whiteSpace: 'nowrap' }}>
                  {new Date(e.createdAt).toLocaleString()}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {e.fromState ? `${e.fromState.replaceAll('_', ' ')} → ` : ''}
                  {e.toState.replaceAll('_', ' ')}
                </td>
                <td
                  className="mono small"
                  title={e.actor}
                  style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {e.actor}
                </td>
                <td className="small">
                  {e.note ?? ''}
                  {e.diff ? ` (edited: ${e.diff.changed.join(', ')})` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmReject !== null}
        danger
        title="Reject this item?"
        body={
          <>
            Reason: <strong>{reason}</strong>. The rejection feeds the agent's guidance and removes the item from the
            queue.
          </>
        }
        confirmLabel="Reject"
        onConfirm={() => void transition(confirmReject!, true)}
        onClose={() => setConfirmReject(null)}
      />
    </div>
  );
}
